import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Bell, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Banner row for active schedule-change notifications.
 * Mounts at the top of counselor + supervisor dashboards.
 *
 * Pass either:
 *   - groupCode="B01"          (single group, counselor case)
 *   - groupCodes={["B01",...]} (multiple groups, supervisor case)
 *
 * Refetches on mount AND on browser tab focus.
 */
export default function ScheduleNotificationsBanner({ groupCode, groupCodes }) {
  const [items, setItems] = useState([]);
  const codes = Array.isArray(groupCodes) ? groupCodes : (groupCode ? [groupCode] : []);
  const codesKey = codes.join(",");

  const load = useCallback(async () => {
    if (!codes.length) return;
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const responses = await Promise.allSettled(
        codes.map((c) => axios.get(`${API}/schedule-notifications/${encodeURIComponent(c)}`, { headers }))
      );
      const merged = [];
      for (const r of responses) {
        if (r.status === "fulfilled" && Array.isArray(r.value.data)) merged.push(...r.value.data);
      }
      // De-dupe:
      //   - camp-wide notifications (is_camp_wide=true) collapse to ONE entry
      //     per batch_id, even when fetched from N different group endpoints
      //     (so a supervisor with 16 groups sees one camp-wide banner, not 16).
      //   - per-group notifications dedupe by id (unchanged behavior).
      const seenIds = new Set();
      const seenBatches = new Set();
      const deduped = [];
      for (const n of merged) {
        if (n.is_camp_wide && n.batch_id) {
          if (seenBatches.has(n.batch_id)) continue;
          seenBatches.add(n.batch_id);
          deduped.push(n);
        } else {
          if (seenIds.has(n.id)) continue;
          seenIds.add(n.id);
          deduped.push(n);
        }
      }
      // Newest first
      deduped.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
      setItems(deduped);
    } catch {
      // silent — banner is best-effort
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codesKey]);

  useEffect(() => {
    load();
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const dismiss = async (item) => {
    // Hide locally first (camp-wide and per-group both filter out by id).
    setItems((prev) => prev.filter((n) => n.id !== item.id));
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      // For camp-wide notifications a single batch_id spans many docs (one per
      // group). Dismissing only the one shown would leave the same banner
      // popping back on the next refetch from a different group's endpoint,
      // so dismiss EVERY doc in the batch the viewer can reach.
      if (item.is_camp_wide && item.batch_id && codes.length > 1) {
        // Resolve all sibling notification ids by hitting each group again.
        const responses = await Promise.allSettled(
          codes.map((c) => axios.get(`${API}/schedule-notifications/${encodeURIComponent(c)}`, { headers }))
        );
        const ids = new Set([item.id]);
        for (const r of responses) {
          if (r.status === "fulfilled" && Array.isArray(r.value.data)) {
            for (const n of r.value.data) {
              if (n.batch_id === item.batch_id) ids.add(n.id);
            }
          }
        }
        await Promise.allSettled(
          Array.from(ids).map((id) =>
            axios.post(`${API}/schedule-notifications/${id}/dismiss`, null, { headers })
          )
        );
      } else {
        await axios.post(`${API}/schedule-notifications/${item.id}/dismiss`, null, { headers });
      }
    } catch {
      // restore on failure so the user can retry
      load();
    }
  };

  if (!items.length) return null;

  return (
    <div className="space-y-2 mb-3" data-testid="schedule-notifications-banner">
      {items.map((n) => (
        <div
          key={n.id}
          className="flex items-start gap-2 rounded-xl border-2 border-amber-300 bg-amber-50 px-3 py-2 text-amber-900 shadow-sm"
          data-testid={`schedule-notification-${n.id}`}
        >
          <Bell className="w-4 h-4 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold">{n.text}</p>
            <p className="text-[10px] text-amber-800/80 mt-0.5">
              {n.created_at ? new Date(n.created_at).toLocaleString("en-US", { timeZone: "America/New_York" }) : ""}
            </p>
          </div>
          <button
            onClick={() => dismiss(n)}
            className="text-amber-700 hover:text-amber-900 p-1 rounded hover:bg-amber-100"
            aria-label="Dismiss notification"
            data-testid={`schedule-notification-dismiss-${n.id}`}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ))}
    </div>
  );
}
